import { CollectionsInsightRenderer, Proposition } from '../../shared/types';

export interface RendererRegistrationKey {
  proposition: Proposition;
  type: string;
}

interface Callback {
  key: string;
  callback: (registrationKey: RendererRegistrationKey, renderer: CollectionsInsightRenderer) => void;
}

class CollectionsInsightRendererRegister {
  private static instance: CollectionsInsightRendererRegister;
  private registry = new Map<string, CollectionsInsightRenderer>();
  private callbackRegistry: Callback[] = [];

  private getRegistrationKey({ proposition, type }: RendererRegistrationKey) {
    return `${proposition}.${type}`;
  }

  public register<TInsightData>(
    registrationKey: RendererRegistrationKey,
    renderer: CollectionsInsightRenderer<TInsightData>
  ): void {
    const key = this.getRegistrationKey(registrationKey);
    if (this.registry.has(key)) {
      throw new Error(`${key} type already registered as CollectionsInsightRender`);
    }

    this.registry.set(key, renderer);
    this.callbackRegistry.filter(cb => cb.key === key).forEach(cb => cb.callback(registrationKey, renderer));
  }

  public get<TInsightData = unknown>(
    registrationKey: RendererRegistrationKey
  ): CollectionsInsightRenderer<TInsightData> | null {
    const key = this.getRegistrationKey(registrationKey);
    const renderer = this.registry.get(key) as CollectionsInsightRenderer<TInsightData>;

    if (!renderer) {
      return null;
    }

    return renderer;
  }

  public registerUpdateCallback(
    registrationKey: RendererRegistrationKey,
    callback: (registrationKey: RendererRegistrationKey, renderer: CollectionsInsightRenderer) => void
  ): void {
    const key = this.getRegistrationKey(registrationKey);

    // avoid the same callback multiple times
    if (!this.callbackRegistry.some(cb => cb.key === key && cb.callback === callback)) {
      this.callbackRegistry.push({ key, callback });
    }
  }

  public static getInstance(): CollectionsInsightRendererRegister {
    if (!CollectionsInsightRendererRegister.instance) {
      CollectionsInsightRendererRegister.instance = new CollectionsInsightRendererRegister();
    }
    return CollectionsInsightRendererRegister.instance;
  }
}

export { CollectionsInsightRendererRegister as CollectionsInsightRenderRegister };
